/**
 * 署名 URL に対する Range 取得そのもの（pdf.js に依存しない層）。
 *
 * 転送クラス（`range-transport.ts`）は pdf.js の `PDFDataRangeTransport` を継承するため、
 * jsdom では import できない（`DOMMatrix` が無い）。**Range が使えるかどうかの判断**は
 * 製品の要件（FR-SRC-02）そのものなので、単体で確かめられるようここに置く。
 */
import { ViewerError } from '../../core/errors';
import type { ManifestSession } from '../../core/manifest';

/** 先頭チャンク長の既定。PDF のヘッダと相当量の交差参照表が収まる程度に取る。 */
export const DEFAULT_CHUNK = 64 * 1024;

/** 署名切れとみなす HTTP ステータス。 */
const EXPIRED_STATUSES = new Set([401, 403, 410]);

/** マニフェストから現在の PDF 署名 URL を取り出す。 */
export function pdfUrl(session: ManifestSession): string {
  const url = session.current.urls.pdf;
  if (!url) {
    throw new ViewerError('E_ACCESS_DENIED', 'PDF の配信 URL が許可されていない');
  }
  return url;
}

export interface RangeResponse {
  body: Uint8Array;
  /** Content-Range から読んだ全体長（不明なら undefined）。 */
  totalLength?: number;
}

/**
 * Range 要求を 1 回だけ再試行つきで発行する。
 * 署名切れ（401/403/410）ならマニフェストを再取得し、新しい URL でやり直す。
 */
export async function fetchRange(
  session: ManifestSession,
  begin: number,
  end: number,
  signal: AbortSignal,
): Promise<RangeResponse> {
  const request = async (url: string): Promise<Response> =>
    fetch(url, {
      signal,
      credentials: 'same-origin',
      headers: { Range: `bytes=${begin}-${end - 1}` },
    });

  let response = await request(pdfUrl(session));
  if (EXPIRED_STATUSES.has(response.status)) {
    await session.refresh();
    response = await request(pdfUrl(session));
  }
  if (response.status !== 206 && response.status !== 200) {
    throw new ViewerError(
      'E_RANGE',
      `PDF の部分取得に失敗（HTTP ${response.status}・${begin}-${end - 1}）`,
    );
  }

  const buffer = new Uint8Array(await response.arrayBuffer());
  const contentRange = response.headers.get('Content-Range');
  const total = contentRange?.match(/\/(\d+)$/)?.[1];
  return total === undefined
    ? { body: buffer }
    : { body: buffer, totalLength: Number.parseInt(total, 10) };
}

/**
 * 先頭チャンクの取得結果から、以後の取り方を決める（FR-SRC-02）。
 *
 * サーバ／プロキシが Range を落とす経路では 206 も `Content-Range` も返らない。そのときの
 * 200 応答は**全体**なので、その本文をそのまま使って全体取得へ落とす（要件の
 * 「Range 非対応時は全体取得に自動フォールバックする」）。
 */
export type TransportInput =
  | { kind: 'range'; totalLength: number; initial: Uint8Array }
  | { kind: 'full'; data: Uint8Array };

export async function openInput(
  session: ManifestSession,
  signal: AbortSignal,
  chunkSize: number = DEFAULT_CHUNK,
): Promise<TransportInput> {
  const first = await fetchRange(session, 0, chunkSize, signal);
  return first.totalLength === undefined
    ? { kind: 'full', data: first.body }
    : { kind: 'range', totalLength: first.totalLength, initial: first.body };
}
